/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createCharge, createServicesCharge, updateCharge } from "../services/api";
import SearchableSelect from "./SearchableSelect";
import "./ChargesForm.css";

const emptyAchatLigneForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  fournisseur: "",
  fournisseurId: "",
  numBonEnlevement: "",
  numeroNexxio: "",
  montantHT: "",
});

const canAddAchatLigne = (form) =>
  Boolean(form?.fournisseurId) &&
  form?.montantHT !== "" &&
  !isNaN(Number(form.montantHT));

// Popup component for editing (fullscreen for personnel)
function EditPopup({ isOpen, onClose, children, fullScreen }) {
  if (!isOpen) return null;
  return (
    <div
      className="popup-overlay"
      style={{
        zIndex: 9999,
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        padding: "1rem",
      }}
    >
      <div
        className="popup-content"
        style={
          fullScreen
            ? {
                width: "min(1200px, 98vw)",
                maxWidth: "98vw",
                minHeight: "80vh",
                maxHeight: "98vh",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                position: "relative",
                padding: "1.5rem 2rem",
                boxSizing: "border-box",
                borderRadius: 8,
                background: "#fff",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              }
            : {
                width: "min(720px, 96vw)",
                maxWidth: "96vw",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
                padding: "1rem",
                boxSizing: "border-box",
                borderRadius: 8,
                background: "#fff",
                boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              }
        }
      >
        <button
          className="popup-close-btn"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            zIndex: 3,
            background: "transparent",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
          }}
          aria-label="Fermer"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

// Lightweight confirmation popup specifically for Transport fees
function ConfirmTransportPopup({ open, amount, isLongChantier, workingDays, onAdd, onSkip }) {
  console.log("ConfirmTransportPopup rendered with open=", open, "amount=", amount, "isLongChantier=", isLongChantier, "workingDays=", workingDays);
  
  if (!open) return null;
  
  const dailyRate = amount || 0;
  const totalAmount = isLongChantier && workingDays > 1 ? dailyRate * workingDays : dailyRate;
  
  return (
    <div
      className="popup-overlay"
      style={{
        zIndex: 10050, // above edit popup
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        padding: "1rem",
      }}
    >
      <div
        className="popup-content"
        style={{
          width: "min(560px, 96vw)",
          maxWidth: "96vw",
          padding: "1.25rem 1.5rem",
          borderRadius: 10,
          background: "#fff",
          boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem 0", color: "#1976d2" }}>
          Ajouter les frais de transport ?
        </h3>
        <p style={{ margin: "0.5rem 0 1rem 0", lineHeight: 1.5 }}>
          Au moins un salarié possède un camion et des frais de transport sont configurés.
          Voulez-vous ajouter les frais de transport de{" "}
          {isLongChantier && workingDays > 1 ? (
            <>
              <strong>{dailyRate.toFixed(2)} € × {workingDays} jours = {totalAmount.toFixed(2)} €</strong>
            </>
          ) : (
            <strong>{totalAmount.toFixed(2)} €</strong>
          )} à cette charge ?
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onSkip}>
            Ne pas ajouter
          </button>
          <button className="btn btn-transport" onClick={onAdd}>
            🚚 Ajouter frais de transport
          </button>
        </div>
      </div>
    </div>
  );
}

// Lightweight confirmation popup for deleting a charge
function ConfirmDeletePopup({ open, name, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div
      className="popup-overlay"
      style={{
        zIndex: 10060,
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        padding: "1rem",
      }}
    >
      <div
        className="popup-content"
        style={{
          width: "min(520px, 96vw)",
          maxWidth: "96vw",
          padding: "1.25rem 1.5rem",
          borderRadius: 10,
          background: "#fff",
          boxShadow: "0 12px 36px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem 0", color: "#d32f2f" }}>
          Supprimer la charge ?
        </h3>
        <p style={{ margin: "0.5rem 0 1rem 0", lineHeight: 1.5 }}>
          Voulez-vous vraiment supprimer
          {name ? <> « <strong>{name}</strong> »</> : ""} ? Cette action est irréversible.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel}>
            Annuler
          </button>
          <button
            className="btn btn-delete"
            onClick={onConfirm}
            style={{ background: "#d32f2f", color: "#fff" }}
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Helpers for robust camion detection and transport config parsing -------
const truthyCamionValues = new Set([
  true, 1, "1", "true", "TRUE", "True", "oui", "OUI", "Oui", "yes", "YES", "Yes", "y", "Y", "o", "O"
]);

function normalizeACamionFlag(val) {
  if (val === undefined || val === null) return false;
  if (typeof val === "string") return truthyCamionValues.has(val.trim());
  return truthyCamionValues.has(val);
}

// Try various common property names coming from different APIs/DBs
function getSalarieHasCamion(salarie) {
  if (!salarie) return false;
  const v =
    salarie.aCamion ??
    salarie.acamion ??
    salarie.camion ??
    salarie.hasCamion ??
    salarie.has_camion ??
    salarie.has_truck ??
    salarie.truck ??
    salarie.vehiculeCamion ??
    salarie.vehicule_camion;
  return normalizeACamionFlag(v);
}

// Robust total extraction for frais_transport_config (sum of all `prix`)
const toNumber = (v) => (isFinite(Number(v)) ? Number(v) : 0);
const sumPrixArray = (arr) => arr.reduce((s, row) => s + toNumber(row?.prix), 0);
function parseTransportConfigToTotal(data) {
  if (Array.isArray(data)) return sumPrixArray(data);
  if (Array.isArray(data?.rows)) return sumPrixArray(data.rows);
  if (Array.isArray(data?.data)) return sumPrixArray(data.data);
  if (Array.isArray(data?.items)) return sumPrixArray(data.items);
  if (Array.isArray(data?.list)) return sumPrixArray(data.list);
  // Fallback: if data is an object, maybe it's a single config item
  if (data && typeof data === 'object' && data.prix) return toNumber(data.prix);
  return 0;
}


async function evaluateBillableForEntries(API_URL, entries, excludeChargeId, setPersonnelEntries) {
  try {
    const payload = {
      entries: (entries || [])
        .filter(e => !e.isTransportFee)
        .map(e => ({
          salarieId: e.salarieId,
          dates: (e.dates || []).map(d => d.date)
        })),
      excludeChargeId: excludeChargeId || undefined
    };
    if (payload.entries.length === 0) return;

    const res = await fetch(`${API_URL}/api/charges/billable/evaluate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return;
    const data = await res.json();
    const map = data?.map || {};

    // Update entries with billable flags
    setPersonnelEntries((prev) =>
      prev.map((e) => {
        if (e.isTransportFee) return e;
        const newDates = (e.dates || []).map((d) => {
          const key = `${e.salarieId}|${d.date}`;
          const billable = map.hasOwnProperty(key) ? !!map[key] : true;
          return { ...d, billable };
        });
        return { ...e, dates: newDates };
      })
    );
  } catch (e) {
    console.warn('billable evaluate failed, proceeding without preflight:', e);
  }
}
const ChargesForm = () => {
  
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedChargeType, setSelectedChargeType] = useState("");
  const [customChargeType, setCustomChargeType] = useState("");
  const [chargeName, setChargeName] = useState("");
  const [budget, setBudget] = useState("");
  const [description, setDescription] = useState("");
  const [charges, setCharges] = useState([]);
  const [chantier, setChantier] = useState(null);
  // Mode d'affichage des charges: 'provisoires' (par défaut, inclut 30%) ou 'réelles' (exclut le 30%)
  const [chargeMode, setChargeMode] = useState("provisoires");

  // New states for dynamic forms
  const [servicesData, setServicesData] = useState({
    "Locations de matériel": 0,
    Echafaudage: 0,
    Levage: 0,
    Transport: 0,
    "Sous-traitant": 0,
    Voirie: 0,
    "Divers 1": 0,
    "Divers 2": 0,
  });
  const [interimOuvriers, setInterimOuvriers] = useState([]);
  const [interimForm, setInterimForm] = useState({
    nom: "",
    heures: "",
    taux: "",
  });

  // Achat detailed: table of line items
  const [achatLignes, setAchatLignes] = useState([]);
  const [achatForm, setAchatForm] = useState(emptyAchatLigneForm());
  const [fournisseurs, setFournisseurs] = useState([]);

  const [salaries, setSalaries] = useState([]);
  const [personnelEntries, setPersonnelEntries] = useState([]);
  const [personnelForm, setPersonnelForm] = useState({
    salarieId: "",
    prestationId: "",
    dateDebut: "",
    dateFin: "",
    heuresParJour: [], // [{ date, heureDebut, heureFin }]
  });
  const [personnelTotal, setPersonnelTotal] = useState(0);
  const [prestations, setPrestations] = useState([]);

  // Transport fee config and flags
  const [fraisTransportTotal, setFraisTransportTotal] = useState(0);
  const [fraisTransportAdded, setFraisTransportAdded] = useState(false);

  // Edit state for inline editing
  const [editingCharge, setEditingCharge] = useState(null);
  const [editPopupOpen, setEditPopupOpen] = useState(false);

  // For personnel entry editing inside popup
  const [editingPersonnelIdx, setEditingPersonnelIdx] = useState(null);
  const [editPersonnelForm, setEditPersonnelForm] = useState(null);

  // Confirmation state for transport fees on save
  const [showTransportConfirm, setShowTransportConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'create' | 'update'
  const [saving, setSaving] = useState(false);

  // Confirmation state for deleting a charge
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
  });

  // Interactive popup state for Achat details (view + add/delete rows)
  const [viewAchatCharge, setViewAchatCharge] = useState(null);
  const [popupAchatLignes, setPopupAchatLignes] = useState([]);
  const [popupAchatForm, setPopupAchatForm] = useState(emptyAchatLigneForm());
  const [popupAchatSaving, setPopupAchatSaving] = useState(false);

  const chargeTypes = [
    "Achat",
    "Services extérieurs",
    "Interim",
    "Charges de personnel",
    "Charges fixes",
    "Autre",
  ];

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Determine chantier mode (long vs short) based on duration > 1 day
  const isLongChantier = useMemo(() => {
    if (!chantier?.dateDebut || !chantier?.dateFin) return false;
    const toYMD = (v) => {
      if (!v) return null;
      if (typeof v === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        if (v.includes('T')) return v.split('T')[0];
      }
      try { return new Date(v).toISOString().slice(0,10); } catch { return null; }
    };
    const ymdStart = toYMD(chantier.dateDebut);
    const ymdEnd = toYMD(chantier.dateFin);
    if (!ymdStart || !ymdEnd) return false;
    const start = new Date(`${ymdStart}T00:00:00Z`);
    const end = new Date(`${ymdEnd}T00:00:00Z`);
    if (isNaN(start) || isNaN(end) || start > end) return false;
    const diffDays = Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1; // inclusive
    return diffDays > 1;
  }, [chantier?.dateDebut, chantier?.dateFin]);

  useEffect(() => {
    fetchChantier();
    fetchCharges();
    fetchSalaries();
    fetchPrestations();
    fetchFournisseurs();
    fetchFraisTransportTotal(); // Fetch transport config once per chantier view
  }, [id]);

  // Initialize personnel form dates when chantier is loaded
  useEffect(() => {
    if (chantier?.dateDebut && chantier?.dateFin && selectedChargeType === "Charges de personnel") {
      const dateDebut = chantier.dateDebut.split('T')[0];
      const dateFin = chantier.dateFin.split('T')[0];
      setPersonnelForm(prev => {
        if (!prev.dateDebut || !prev.dateFin) {
          console.log('Auto-initializing dates from chantier:', { dateDebut, dateFin });
          return { ...prev, dateDebut, dateFin };
        }
        return prev;
      });
    }
  }, [chantier, selectedChargeType]);

const fetchChantier = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chantiers/${id}`);
      const data = await response.json();
      setChantier(data);
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  const fetchCharges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/charges/chantier/${id}`);
      const data = await response.json();
      console.log('📦 Fetched charges:', data.length, 'total');
      data.filter(c => c.type === 'Achat').forEach(c => {
        console.log(`  Achat: id=${c._id}, name="${c.name}", isReelle=${c.isReelle}, budget=${c.budget}`);
      });
      setCharges(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  const fetchSalaries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/salaries`);
      const data = await res.json();
      setSalaries(Array.isArray(data) ? data : []);
    } catch (e) {
      setSalaries([]);
    }
  };

  const fetchPrestations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prestations`);
      const data = await res.json();
      setPrestations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching prestations:", e);
      setPrestations([]);
    }
  };

  const fetchFournisseurs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/fournisseurs`);
      const data = await res.json();
      setFournisseurs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching fournisseurs:", e);
      setFournisseurs([]);
    }
  };

const fetchFraisTransportTotal = async () => {
  console.log("Fetching frais transport config...");
  const endpoints = [
    `${API_URL}/api/frais-transport-config`,
    `${API_URL}/api/frais_transport_config`,
    `${API_URL}/api/fraisTransportConfig`,
  ];
  
  for (const url of endpoints) {
    try {
      console.log(`Trying endpoint: ${url}`);
      const res = await fetch(url);
      if (!res.ok) {
        console.log(`Endpoint ${url} returned ${res.status}`);
        continue;
      }
      
      const data = await res.json();
      console.log(`Received transport config data:`, data);
      
      const total = parseTransportConfigToTotal(data);
      console.log(`Calculated transport total: ${total}`);
      
      setFraisTransportTotal(isFinite(total) ? total : 0);
      console.log(`Set fraisTransportTotal to: ${isFinite(total) ? total : 0}`);
      return;
    } catch (e) {
      console.error(`Error fetching from ${url}:`, e);
    }
  }
  
  console.warn("Failed to fetch transport config from any endpoint, setting total to 0");
  setFraisTransportTotal(0);
};

  const handleChargeTypeChange = (e) => {
    const value = e.target.value;
    setSelectedChargeType(value);
    setCustomChargeType("");
    setChargeName("");
    setBudget("");
    setDescription("");
    setServicesData({
      "Locations de matériel": 0,
      Echafaudage: 0,
      Levage: 0,
      Transport: 0,
      "Sous-traitant": 0,
      Voirie: 0,
      "Divers 1": 0,
      "Divers 2": 0,
    });
    setInterimOuvriers([]);
    setInterimForm({ nom: "", heures: "", taux: "" });
    setAchatLignes([]);
    setAchatForm(emptyAchatLigneForm());
    setFraisTransportAdded(false);
    // Only reset personnelEntries if not editing
    if (!editingCharge) {
      resetPersonnelForm();
    }
    // Initialize dates for personnel form when selecting Charges de personnel
    if (value === "Charges de personnel") {
      const dateDebut = chantier?.dateDebut ? chantier.dateDebut.split('T')[0] : '';
      const dateFin = chantier?.dateFin ? chantier.dateFin.split('T')[0] : '';
      console.log('Initializing personnelForm with dates:', { dateDebut, dateFin });
      setPersonnelForm(prev => ({ ...prev, dateDebut, dateFin }));
    }
  };

  // --- Services extérieurs handlers ---
  const handleServicesChange = (key, value) => {
    setServicesData((prev) => ({
      ...prev,
      [key]: Number(value),
    }));
  };
  const servicesTotal = Object.values(servicesData).reduce((a, b) => a + b, 0);

  // --- Interim handlers ---
  const handleInterimFormChange = (e) => {
    setInterimForm({
      ...interimForm,
      [e.target.name]: e.target.value,
    });
  };
  const addInterimOuvrier = () => {
    if (
      interimForm.nom &&
      interimForm.heures &&
      interimForm.taux &&
      !isNaN(Number(interimForm.heures)) &&
      !isNaN(Number(interimForm.taux))
    ) {
      setInterimOuvriers([
        ...interimOuvriers,
        {
          ...interimForm,
          heures: Number(interimForm.heures),
          taux: Number(interimForm.taux),
          cout: Number(interimForm.heures) * Number(interimForm.taux),
        },
      ]);
      setInterimForm({ nom: "", heures: "", taux: "" });
    }
  };
  const interimTotal = interimOuvriers.reduce((sum, o) => sum + o.cout, 0);

  // --- Achat handlers (table-based with detailed line items) ---
  const handleAchatFormChange = (e) => {
    setAchatForm({
      ...achatForm,
      [e.target.name]: e.target.value,
    });
  };

  const addAchatLigne = () => {
    if (!canAddAchatLigne(achatForm)) return;
    const selected = fournisseurs.find(
      (f) => String(f.id || f._id) === String(achatForm.fournisseurId)
    );
    setAchatLignes([
      ...achatLignes,
      {
        ...achatForm,
        fournisseurId: achatForm.fournisseurId,
        fournisseur: selected?.name || selected?.nom || achatForm.fournisseur,
        montantHT: parseFloat(achatForm.montantHT),
      },
    ]);
    setAchatForm(emptyAchatLigneForm());
  };

  const removeAchatLigne = (idx) => {
    setAchatLignes(achatLignes.filter((_, i) => i !== idx));
  };

  const achatTotal = achatLignes.reduce((sum, l) => sum + Number(l.montantHT || 0), 0);

  // --- Popup Achat handlers (for interactive view popup) ---
  const openAchatPopup = (charge) => {
    setViewAchatCharge(charge);
    // If old charge has no achatLignes but has a non-zero montant, create an initial row
    let lignes = Array.isArray(charge.achatLignes) ? [...charge.achatLignes] : [];
    if (lignes.length === 0 && Number(charge.budget || charge.montant || 0) > 0) {
      const rawDate = charge.date || new Date().toISOString().slice(0, 10);
      const cleanDate = rawDate.includes("T") ? rawDate.split("T")[0] : rawDate;
      lignes = [{
        date: cleanDate,
        fournisseur: "",
        fournisseurId: "",
        numBonEnlevement: "",
        numeroNexxio: "",
        montantHT: Number(charge.budget || charge.montant || 0),
      }];
    }
    setPopupAchatLignes(lignes);
    setPopupAchatForm(emptyAchatLigneForm());
  };

  const closeAchatPopup = () => {
    setViewAchatCharge(null);
    setPopupAchatLignes([]);
    setPopupAchatForm(emptyAchatLigneForm());
  };

  const handlePopupAchatFormChange = (e) => {
    setPopupAchatForm({ ...popupAchatForm, [e.target.name]: e.target.value });
  };

  const addPopupAchatLigne = () => {
    if (!canAddAchatLigne(popupAchatForm)) return;
    const selected = fournisseurs.find(
      (f) => String(f.id || f._id) === String(popupAchatForm.fournisseurId)
    );
    setPopupAchatLignes([
      ...popupAchatLignes,
      {
        ...popupAchatForm,
        fournisseurId: popupAchatForm.fournisseurId,
        fournisseur: selected?.name || selected?.nom || popupAchatForm.fournisseur,
        montantHT: parseFloat(popupAchatForm.montantHT),
      },
    ]);
    setPopupAchatForm(emptyAchatLigneForm());
  };

  const removePopupAchatLigne = (idx) => {
    setPopupAchatLignes(popupAchatLignes.filter((_, i) => i !== idx));
  };

  const popupAchatTotal = popupAchatLignes.reduce((sum, l) => sum + Number(l.montantHT || 0), 0);

  const savePopupAchatChanges = async () => {
    if (!viewAchatCharge || !viewAchatCharge._id) return;
    setPopupAchatSaving(true);
    try {
      const totalHT = popupAchatLignes.reduce((sum, l) => sum + Number(l.montantHT || 0), 0);
      const payload = {
        chantierId: id,
        type: "Achat",
        name: viewAchatCharge.name || "Achat",
        montant: totalHT,
        budget: totalHT,
        description: viewAchatCharge.description || "",
        achatLignes: popupAchatLignes,
      };
      await updateCharge(viewAchatCharge._id, payload);
      await fetchCharges();
      closeAchatPopup();
    } catch (error) {
      console.error("Error saving achat changes:", error);
      alert("Erreur lors de la sauvegarde des modifications.");
    } finally {
      setPopupAchatSaving(false);
    }
  };

  // Helper to get all dates between two dates
  const getDatesInRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handlePersonnelFormChange = (e) => {
    const { name, value } = e.target;
    console.log('handlePersonnelFormChange:', { name, value });
    setPersonnelForm((prev) => {
      let updated = { ...prev, [name]: value };
      // Auto-generate heuresParJour when both salarieId and prestationId are selected
      if ((name === "salarieId" || name === "prestationId") &&
          updated.salarieId && updated.prestationId &&
          updated.dateDebut && updated.dateFin) {
        console.log('Auto-generating heuresParJour for dates:', updated.dateDebut, 'to', updated.dateFin);
        if (isLongChantier) {
          // Pre-populate working days (Mon-Fri excluding French public holidays)
          const days = enumerateWorkingDays(updated.dateDebut, updated.dateFin);
          console.log('Long chantier: generated', days.length, 'working days');
          updated.heuresParJour = days.map((ds) => ({ date: ds, selected: true }));
        } else {
          const dates = getDatesInRange(updated.dateDebut, updated.dateFin);
          console.log('Short chantier: generated', dates.length, 'dates');
          updated.heuresParJour = dates.map((d) => ({
            date: d.toISOString().split("T")[0],
            heureDebut: "",
            heureFin: "",
          }));
        }
      }
      console.log('Updated personnelForm:', updated);
      return updated;
    });
  };

  const handleHeureChange = (idx, field, value) => {
    setPersonnelForm((prev) => {
      const heuresParJour = [...prev.heuresParJour];
      heuresParJour[idx][field] = value;
      return { ...prev, heuresParJour };
    });
  };


const isValidWorkDay = (h) => {
  const debut = Number(h.heureDebut);
  const fin = Number(h.heureFin);
  return !isNaN(debut) && !isNaN(fin) && fin > debut;
};

const isBillableDay = (h) => {
  // Default to billable unless explicitly false
  return !(h && h.billable === false);
};

const computeRealHoursFromDates = (dates) => {
  if (!Array.isArray(dates)) return 0;
  let total = 0;
  dates.forEach((h) => {
    const debut = Number(h.heureDebut);
    const fin = Number(h.heureFin);
    if (!isNaN(debut) && !isNaN(fin) && fin > debut) {
      total += fin - debut;
    }
  });
  return total;
};

const computeBilledHoursFromDates = (dates) => {
  if (!Array.isArray(dates)) return 0;
  let days = 0;
  dates.forEach((h) => {
    if (isValidWorkDay(h) && isBillableDay(h)) {
      days += 1;
    }
  });
  return days * 7; // 7h per valid, billable worked day
};  
  const getRateForEntry = (entry) => {
    // First try to get rate from the stored salarie.taux_horaire
    if (entry?.salarie?.taux_horaire != null && !isNaN(Number(entry.salarie.taux_horaire))) {
      return Number(entry.salarie.taux_horaire);
    }
    // Fallback to entry.tauxHoraire if salarie object not available
    let rate = Number(entry?.tauxHoraire || 0);
    return rate;
  };

  const computeBilledTotalForEntry = useCallback((entry) => {
    // Special handling for the virtual "frais de transport" entry
    if (entry?.isTransportFee) {
      return Number(entry.amount ?? entry.total ?? 0);
    }
    if (isLongChantier) {
      const days = Array.isArray(entry?.dates)
        ? entry.dates.reduce((cnt, d) => {
            if (!d?.date) return cnt;
            // billable defaults to true unless explicitly false; also honor optional selected=true/false
            const selected = d.selected !== false; 
            const billable = d.billable !== false;
            // Only count working days; reuse local helper
            return selected && billable && isWorkingDay(d.date) ? cnt + 1 : cnt;
          }, 0)
        : 0;
      return 140 * days;
    }
    const rate = getRateForEntry(entry);
    const billedHours = computeBilledHoursFromDates(entry?.dates);
    return rate * billedHours;
  }, [isLongChantier, getRateForEntry, computeBilledHoursFromDates, isWorkingDay]);

  // Recalculate personnelTotal whenever personnelEntries changes
  useEffect(() => {
    if (selectedChargeType === "Charges de personnel" && personnelEntries.length > 0) {
      const total = personnelEntries.reduce((sum, p) => sum + computeBilledTotalForEntry(p), 0);
      setPersonnelTotal(total);
    }
  }, [personnelEntries, selectedChargeType, computeBilledTotalForEntry]);

function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}
function addDaysUTC(dateUTC, days) {
  const d = new Date(dateUTC.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}
function fmtDateUTC(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function frenchPublicHolidays(year) {
  const easter = easterDate(year);
  const easterMonday = addDaysUTC(easter, 1);
  const ascension = addDaysUTC(easter, 39);
  const pentecostMonday = addDaysUTC(easter, 50);
  return new Set([
    `${year}-01-01`,
    `${year}-05-01`,
    `${year}-05-08`,
    `${year}-07-14`,
    `${year}-08-15`,
    `${year}-11-01`,
    `${year}-11-11`,
    `${year}-12-25`,
    fmtDateUTC(easterMonday),
    fmtDateUTC(ascension),
    fmtDateUTC(pentecostMonday),
  ]);
}
function isWeekendUTC(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDay();
  return day === 0 || day === 6;
}
function isFrenchHoliday(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const hols = frenchPublicHolidays(d.getUTCFullYear());
  return hols.has(dateStr);
}
function isWorkingDay(dateStr) {
  return !isWeekendUTC(dateStr) && !isFrenchHoliday(dateStr);
}
function enumerateDatesInclusiveUTC(startStr, endStr) {
  const arr = [];
  if (!startStr || !endStr) return arr;
  const start = new Date(`${startStr}T00:00:00Z`);
  const end = new Date(`${endStr}T00:00:00Z`);
  if (isNaN(start) || isNaN(end) || start > end) return arr;
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    arr.push(fmtDateUTC(d));
  }
  return arr;
}
function enumerateWorkingDays(startStr, endStr) {
  return enumerateDatesInclusiveUTC(startStr, endStr).filter(isWorkingDay);
}

const addPersonnelEntry = async () => {
    console.log('=== addPersonnelEntry CALLED ===');
    console.log('personnelForm:', personnelForm);
    console.log('isLongChantier:', isLongChantier);
    
    const salarie = salaries.find(
      (s) => String(s._id ?? s.id) === String(personnelForm.salarieId)
    );
    const prestation = prestations.find(
      (p) => String(p.id ?? p._id) === String(personnelForm.prestationId)
    );
    
    console.log('Found salarie:', salarie);
    console.log('Found prestation:', prestation);
    console.log('salarie.taux_horaire:', salarie?.taux_horaire);
    
    if (!salarie || !prestation || !personnelForm.dateDebut || !personnelForm.dateFin) {
      console.error('Missing required data:', { salarie: !!salarie, prestation: !!prestation, dateDebut: !!personnelForm.dateDebut, dateFin: !!personnelForm.dateFin });
      return;
    }

    if (!isLongChantier) {
      if (
        personnelForm.heuresParJour.length === 0 ||
        personnelForm.heuresParJour.some(
          (h) =>
            h.heureDebut === "" ||
            h.heureFin === "" ||
            isNaN(Number(h.heureDebut)) ||
            isNaN(Number(h.heureFin))
        )
      ) {
        console.error('Invalid heuresParJour for short chantier');
        return;
      }
    } else {
      // Long chantier: ensure at least one working day selected
      const countSelected = (personnelForm.heuresParJour || []).filter(d => d.selected !== false).length;
      console.log('Long chantier - selected days:', countSelected);
      if (countSelected <= 0) {
        console.error('No working days selected for long chantier');
        return;
      }
    }

    const realTotalHeures = isLongChantier ? 0 : computeRealHoursFromDates(personnelForm.heuresParJour);
    const billedHours = isLongChantier ? 0 : computeBilledHoursFromDates(personnelForm.heuresParJour);

    console.log('Calculated hours:', { realTotalHeures, billedHours });

    if (!isLongChantier && (realTotalHeures <= 0 || billedHours <= 0)) {
      console.error('Invalid hours calculation:', { realTotalHeures, billedHours });
      return;
    }

    const tauxHoraireNum = Number(salarie.taux_horaire || 0);
    console.log('tauxHoraireNum:', tauxHoraireNum);
    const hasCamion = getSalarieHasCamion(salarie);
    console.log('hasCamion:', hasCamion);
    
    const newEntry = {
      salarie,
      salarieId: salarie._id ?? salarie.id,
      nom: salarie.nom,
      prestationType: prestation.name,
      prestationId: prestation.id ?? prestation._id,
      tauxHoraire: tauxHoraireNum,
      totalHeures: realTotalHeures,
      dates: isLongChantier
        ? personnelForm.heuresParJour.filter(d => d.selected !== false).map((d) => ({ date: d.date }))
        : personnelForm.heuresParJour.map((h) => ({
            date: h.date,
            heureDebut: h.heureDebut,
            heureFin: h.heureFin,
            billable: true, // default: will be corrected by server or preflight
          })),
      dateDebut: personnelForm.dateDebut,
      dateFin: personnelForm.dateFin,
      aCamion: hasCamion,
      longChantier: isLongChantier,
    };

    console.log('Creating newEntry:', newEntry);

    const updatedEntries = [...personnelEntries, newEntry];
    console.log('Setting personnelEntries to:', updatedEntries.length, 'entries');
    setPersonnelEntries(updatedEntries);

    // Optional preflight: mark billable=false if already assigned elsewhere for the same day
    await evaluateBillableForEntries(API_URL, updatedEntries, editingCharge?._id, setPersonnelEntries);

    console.log('Resetting personnelForm');
    setPersonnelForm({
      salarieId: "",
      prestationId: "",
      dateDebut: "",
      dateFin: "",
      heuresParJour: [],
    });
    console.log('=== addPersonnelEntry COMPLETE ===');
  };


const hasEligibleCamion = useMemo(() => {
  console.log('Computing hasEligibleCamion for entries:', personnelEntries);
  if (!Array.isArray(personnelEntries) || personnelEntries.length === 0) return false;
  
  const result = personnelEntries.some(entry => {
    const hasCamion = getSalarieHasCamion(entry.salarie) || normalizeACamionFlag(entry.aCamion);
    console.log(`Entry ${entry.nom}: hasCamion = ${hasCamion}`);
    return hasCamion;
  });
  
  console.log('hasEligibleCamion result:', result);
  return result;
}, [personnelEntries]);

  // Calculate number of working days for long chantiers (based on personnel entries)
  const calculateWorkingDaysForTransport = () => {
    if (!isLongChantier) return 1; // For short chantiers, transport fee is not multiplied
    
    // Get the maximum number of working days from all personnel entries
    let maxWorkingDays = 0;
    personnelEntries.forEach(entry => {
      if (!entry.isTransportFee && Array.isArray(entry.dates)) {
        const workingDays = entry.dates.filter(d => d?.date && isWorkingDay(d.date)).length;
        maxWorkingDays = Math.max(maxWorkingDays, workingDays);
      }
    });
    
    // If no personnel entries yet, use the currently selected days in the form
    if (maxWorkingDays === 0 && personnelForm.heuresParJour.length > 0) {
      maxWorkingDays = personnelForm.heuresParJour.filter(d => d.selected !== false && isWorkingDay(d.date)).length;
    }
    
    return Math.max(1, maxWorkingDays); // At least 1 day
  };

  // Add virtual "frais de transport" entry
  const addFraisTransport = () => {
    if (fraisTransportAdded || fraisTransportTotal <= 0) return;
    
    const workingDays = calculateWorkingDaysForTransport();
    const transportAmount = isLongChantier ? fraisTransportTotal * workingDays : fraisTransportTotal;
    
    setPersonnelEntries((prev) => [
      ...prev,
      {
        isTransportFee: true,
        nom: "Frais de transport",
        prestationType: "Transport",
        tauxHoraire: 0,
        totalHeures: 0,
        dates: [],
        workingDays: workingDays,
        amount: Number(transportAmount),
      },
    ]);
    setFraisTransportAdded(true);
  };

  useEffect(() => {
    // Sum totals across entries (billed rules + possible transport fee)
    setPersonnelTotal(
      personnelEntries.reduce((sum, p) => sum + computeBilledTotalForEntry(p), 0)
    );
  }, [personnelEntries, computeBilledTotalForEntry]);

  const resetPersonnelForm = () => {
    setPersonnelEntries([]);
    // Auto-populate dates from chantier (date only, not time)
    const dateDebut = chantier?.dateDebut ? chantier.dateDebut.split('T')[0] : '';
    const dateFin = chantier?.dateFin ? chantier.dateFin.split('T')[0] : '';
    setPersonnelForm({ salarieId: "", prestationId: "", dateDebut, dateFin, heuresParJour: [] });
    setPersonnelTotal(0);
    setFraisTransportAdded(false);
  };

  // Helper to build personnel payload and recalculated budget
  const buildPersonnelPayload = (includeTransport) => {
    let entries = [...personnelEntries];
    const alreadyHasTransport = entries.some((e) => e.isTransportFee === true);
    if (includeTransport && fraisTransportTotal > 0 && !alreadyHasTransport) {
      const workingDays = calculateWorkingDaysForTransport();
      const transportAmount = isLongChantier ? fraisTransportTotal * workingDays : fraisTransportTotal;
      
      entries = [
        ...entries,
        {
          isTransportFee: true,
          nom: "Frais de transport",
          prestationType: "Transport",
          tauxHoraire: 0,
          totalHeures: 0,
          dates: [],
          workingDays: workingDays,
          amount: Number(transportAmount),
        },
      ];
    }
    const personnel = entries.map((p) => ({
      ...p,
      // Make sure salarie object isn't passed into the final payload if it's large
      salarie: undefined,
      aCamion: getSalarieHasCamion(p.salarie),
      total: computeBilledTotalForEntry(p),
      totalHeures: p.isTransportFee ? 0 : (isLongChantier ? 0 : computeRealHoursFromDates(p.dates)),
    }));
    const recalculatedBudget = personnel.reduce(
      (sum, p) => sum + Number(p.total ?? computeBilledTotalForEntry(p)),
      0
    );
    return { personnel, recalculatedBudget };
  };

const handleSaveCharge = async () => {
  console.log("🚀 handleSaveCharge called for type:", selectedChargeType);
  
  let newCharge;

  if (selectedChargeType === "Services extérieurs") {
    try {
      const response = await createServicesCharge(id, servicesData, description);
      if (response) {
        fetchCharges();
        resetForm();
      }
      return;
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde des services extérieurs: " + error.message);
      return;
    }
  } else if (selectedChargeType === "Interim") {
    newCharge = {
      chantierId: id,
      type: selectedChargeType,
      name: "Interim",
      budget: interimTotal,
      description,
      ouvriers: interimOuvriers,
    };
  } else if (selectedChargeType === "Achat") {
    if (achatLignes.length === 0) {
      alert("Veuillez ajouter au moins une ligne d'achat.");
      return;
    }
    newCharge = {
      chantierId: id,
      type: selectedChargeType,
      name: "Achat",
      montant: achatTotal,
      budget: achatTotal,
      description,
      achatLignes: achatLignes,
    };
  } else if (selectedChargeType === "Charges de personnel") {
    console.log("💼 Processing personnel charges...");
    
    if (personnelEntries.length === 0) {
      alert("Veuillez ajouter au moins un salarié.");
      return;
    }

    console.log("🚚 Transport condition check:", {
      hasEligibleCamion,
      fraisTransportTotal,
      fraisTransportAdded,
      shouldPrompt: hasEligibleCamion && fraisTransportTotal > 0 && !fraisTransportAdded
    });

    const shouldPromptTransport =
      hasEligibleCamion && fraisTransportTotal > 0 && !fraisTransportAdded;

    if (shouldPromptTransport) {
      console.log("✅ Should prompt for transport - setting up popup");
      setPendingAction("create");
      setShowTransportConfirm(true);
      console.log("✅ showTransportConfirm set to true, returning early");
      return;
    }

    console.log("ℹ️ No transport prompt needed, proceeding with save");

    const { personnel, recalculatedBudget } = buildPersonnelPayload(false);
    newCharge = {
      chantierId: id,
      type: selectedChargeType,
      name: "Charges de personnel",
      budget: recalculatedBudget,
      description,
      personnel,
    };
  } else {
    if (!selectedChargeType || !chargeName || !budget) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }
    newCharge = {
      chantierId: id,
      type: selectedChargeType,
      customType:
        selectedChargeType === "Autre" ? customChargeType : undefined,
      name: chargeName,
      budget: parseFloat(budget),
      description,
    };
  }

  if (newCharge) {
    console.log("💾 Saving charge:", newCharge);
    try {
      const response = await createCharge(newCharge);

      if (response) {
        fetchCharges();
        resetForm();
        resetPersonnelForm();
      }
    } catch (error) {
      console.error("Erreur:", error);
    }
  }
};
  const resetForm = () => {
    setSelectedChargeType("");
    setCustomChargeType("");
    setChargeName("");
    setBudget("");
    setDescription("");
    setServicesData({
      "Locations de matériel": 0,
      Echafaudage: 0,
      Levage: 0,
      Transport: 0,
      "Sous-traitant": 0,
      Voirie: 0,
      "Divers 1": 0,
      "Divers 2": 0,
    });
    setInterimOuvriers([]);
    setInterimForm({ nom: "", heures: "", taux: "" });
    setAchatLignes([]);
    setAchatForm(emptyAchatLigneForm());
    setFraisTransportAdded(false);
    resetPersonnelForm();
  };

  // Local delete to avoid name clash with imported deleteCharge
  const deleteCharge = async (chargeId) => {
    try {
      await fetch(`${API_URL}/api/charges/${chargeId}`, {
        method: "DELETE",
      });
      fetchCharges();
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  // Open/close/confirm delete handlers
  const openDeleteConfirm = (charge) => {
    setConfirmDelete({
      open: true,
      id: charge._id,
      name: charge.name || "",
    });
  };
  const closeDeleteConfirm = () => {
    setConfirmDelete({ open: false, id: null, name: "" });
  };
  const handleConfirmDelete = async () => {
    const idToDelete = confirmDelete.id;
    if (!idToDelete) return;
    try {
      await deleteCharge(idToDelete);
    } finally {
      closeDeleteConfirm();
    }
  };

  // Détecte la charge Achat auto-créée (30% du chiffre d'affaire)
  function isAutoThirtyPercentCharge(charge) {
    if (!charge || charge.type !== "Achat") return false;
    if (charge.isAutoThirtyPercent === true) return true;
    const name = String(charge.name || "").toLowerCase();
    const desc = String(charge.description || "").toLowerCase();
    return (
      name.includes("30%") ||
      name.includes("acompte budget") ||
      desc.includes("30%") ||
      desc.includes("chiffre d'affaire") ||
      // broaden detection to match backend auto description
      desc.includes("ajout automatique") ||
      name.includes("budget travaux") ||
      desc.includes("budget travaux")
    );
  }

  // Normalize isReelle coming from API (could be true/"true"/1/"1")
  function isReelleTruthy(charge) {
    const v = charge?.isReelle;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  const calculateTotals = () => {
    const totals = {
      Achat: 0,
      "Services extérieurs": 0,
      Interim: 0,
      "Charges de personnel": 0,
      "Charges fixes": 0,
      Autre: 0,
      total: 0,
    };

    console.log(`💰 Calculating totals for mode: ${chargeMode}`);
    charges.forEach((charge) => {
      // En mode "provisoires", on exclut les charges marquées comme réelles (isReelle)
      if (chargeMode === "provisoires" && isReelleTruthy(charge)) {
        console.log(`  ⏭️ Skipping in provisoires (isReelle=true): ${charge.name}`);
        return;
      }
      // En mode "réelles", on exclut la charge Achat auto-créée (30%) PROVISOIRE uniquement
      // Si la charge est marquée comme réelle, on l'affiche même si son nom/description contient "30%"
      if (chargeMode === "réelles" && isAutoThirtyPercentCharge(charge) && !isReelleTruthy(charge)) {
        console.log(`  ⏭️ Skipping in réelles (auto 30%): ${charge.name}`);
        return;
      }

      const type = charge.type === "Autre" ? "Autre" : charge.type;
      const amount =
        typeof charge.montant !== "undefined"
          ? Number(charge.montant) || 0
          : Number(charge.budget) || 0;
      console.log(`  ✅ Including: ${charge.name} (${type}) = ${amount}€`);
      totals[type] += amount;
      totals.total += amount;
    });

    console.log('Total calculated:', totals.total);
    return totals;
  };

  const totals = calculateTotals();

const handleEditClick = async (charge) => {
    setEditingCharge(charge);
    setEditPopupOpen(true);
    setSelectedChargeType(charge.type);
    setCustomChargeType(charge.customType || "");
    setChargeName(charge.name || "");
    setBudget(charge.budget?.toString() || "");
    setDescription(charge.description || "");
    setFraisTransportAdded(false);
    if (charge.type === "Services extérieurs") {
      setServicesData(
        charge.details || {
          "Locations de matériel": 0,
          Echafaudage: 0,
          Levage: 0,
          Transport: 0,
          "Sous-traitant": 0,
          Voirie: 0,
          "Divers 1": 0,
          "Divers 2": 0,
        }
      );
    } else if (charge.type === "Interim") {
      setInterimOuvriers(charge.ouvriers || []);
    } else if (charge.type === "Achat") {
      // Load achat line items from the charge
      const lignes = Array.isArray(charge.achatLignes) ? charge.achatLignes : [];
      setAchatLignes(lignes);
      setAchatForm(emptyAchatLigneForm());
    } else if (charge.type === "Charges de personnel") {
      const incoming = Array.isArray(charge.personnel) ? charge.personnel : [];
      const enriched = incoming.map((p) => {
        if (p.isTransportFee) return p;
        const salarie = p.salarie || salaries.find(s => String(s._id ?? s.id) === String(p.salarieId));
        // Update tauxHoraire to match salarie's current taux_horaire (in case it changed)
        const updatedTauxHoraire = salarie?.taux_horaire != null ? Number(salarie.taux_horaire) : Number(p.tauxHoraire || 0);
        return { ...p, salarie, tauxHoraire: updatedTauxHoraire };
      });
      setPersonnelEntries(enriched);
      setFraisTransportAdded(enriched.some((p) => p.isTransportFee === true));
      setPersonnelTotal(
        enriched.reduce((sum, p) => sum + computeBilledTotalForEntry(p), 0)
      );

      // Optional preflight on edit load (will correct billable flags if needed)
      await evaluateBillableForEntries(API_URL, enriched, charge._id, setPersonnelEntries);
    }
  };
  // Update charge handler (for popup)
  const handleUpdateCharge = async () => {
    if (!editingCharge || !editingCharge._id) return;
    
    console.log('🔧 handleUpdateCharge called for charge:', {
      id: editingCharge._id,
      type: selectedChargeType,
      editingChargeName: editingCharge?.name,
      editingChargeDesc: editingCharge?.description
    });
    
    let updatedCharge;

    if (selectedChargeType === "Services extérieurs") {
      try {
        const servicesTotal = Object.values(servicesData).reduce((a, b) => a + Number(b || 0), 0);
        updatedCharge = {
          chantierId: id,
          type: selectedChargeType,
          name: "Services extérieurs",
          budget: servicesTotal,
          montant: servicesTotal,
          description,
          details: servicesData,
        };
      } catch (error) {
        alert("Erreur lors de la préparation des données: " + error.message);
        return;
      }
    } else if (selectedChargeType === "Interim") {
      updatedCharge = {
        chantierId: id,
        type: selectedChargeType,
        name: "Interim",
        budget: interimTotal,
        description,
        ouvriers: interimOuvriers,
      };
    } else if (selectedChargeType === "Achat") {
      if (achatLignes.length === 0) {
        alert("Veuillez ajouter au moins une ligne d'achat.");
        return;
      }
      const totalHT = achatLignes.reduce((sum, l) => sum + Number(l.montantHT || 0), 0);
      updatedCharge = {
        chantierId: id,
        type: selectedChargeType,
        name: editingCharge?.name || "Achat",
        montant: totalHT,
        budget: totalHT,
        description: editingCharge?.description || description,
        achatLignes: achatLignes,
      };
      console.log('🔧 Achat charge update payload:', updatedCharge);
    } else if (selectedChargeType === "Charges de personnel") {
      if (personnelEntries.length === 0) {
        alert("Veuillez ajouter au moins un salarié.");
        return;
      }

      const shouldPromptTransport =
        hasEligibleCamion && fraisTransportTotal > 0 && !fraisTransportAdded;

      if (shouldPromptTransport) {
        setPendingAction("update");
        setShowTransportConfirm(true);
        return; // interrupt update until user decides
      }

      const { personnel, recalculatedBudget } = buildPersonnelPayload(false);
      updatedCharge = {
        chantierId: id,
        type: selectedChargeType,
        name: "Charges de personnel",
        budget: recalculatedBudget,
        description,
        personnel,
      };
    } else {
      if (!selectedChargeType || !chargeName || !budget) {
        alert("Veuillez remplir tous les champs obligatoires");
        return;
      }
      updatedCharge = {
        chantierId: id,
        type: selectedChargeType,
        customType:
          selectedChargeType === "Autre" ? customChargeType : undefined,
        name: chargeName,
        budget: parseFloat(budget),
        description,
      };
    }

    try {
      console.log('🔧 Calling updateCharge API with:', { id: editingCharge._id, payload: updatedCharge });
      const resp = await updateCharge(editingCharge._id, updatedCharge);
      console.log('🔧 updateCharge response:', resp);

      // Handle both cases: service may return a Response or a JSON/boolean
      if (resp && typeof resp.ok === "boolean") {
        if (!resp.ok) {
          let msg = "Erreur lors de la sauvegarde";
          try {
            const data = await resp.json();
            msg = data?.message || msg;
          } catch {}
          alert(msg);
          return;
        }
      }

      await fetchCharges();
      setEditPopupOpen(false);
      setEditingCharge(null);
      resetForm();
      resetPersonnelForm();
    } catch (error) {
      console.error("Erreur:", error);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditPopupOpen(false);
    setEditingCharge(null);
    resetForm();
    // Only reset personnel form if not editing
    if (!editingCharge) {
      resetPersonnelForm();
    }
  };

  // Remove a personnel entry when editing a charge
  const removePersonnelEntry = (idx) => {
    const entry = personnelEntries[idx];
    const updated = personnelEntries.filter((_, i) => i !== idx);
    setPersonnelEntries(updated);
    if (entry?.isTransportFee) {
      setFraisTransportAdded(false);
    }
  };

  // Edit a personnel entry (open a sub-form for editing)
  const startEditPersonnelEntry = (idx) => {
    const entry = personnelEntries[idx];
    if (entry?.isTransportFee) {
      // no edit for transport fee
      return;
    }
    setEditingPersonnelIdx(idx);
    setEditPersonnelForm({ ...personnelEntries[idx] });
  };

  // When changing prestation in personnel edit, keep the salarié's taux_horaire
  const handleEditPersonnelFormChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...editPersonnelForm, [name]: value };
    if (name === "prestationType") {
      const prestationObj = prestations.find((p) => p.name === value);
      if (prestationObj) {
        // Keep the salarié's taux_horaire, not the prestation's prix_heure
        updated.prestationId = prestationObj.id ?? prestationObj._id;
      }
    }
    // Recompute billed total regardless of which field changed (7h/day billing)
    // Use the salarié's taux_horaire from the entry
    const tx = getRateForEntry(updated);
    const billedHours = computeBilledHoursFromDates(updated.dates);
    updated.total = tx * billedHours;

    setEditPersonnelForm(updated);
  };

  const saveEditPersonnelEntry = () => {
    const tx = getRateForEntry(editPersonnelForm);
    const billedHours = computeBilledHoursFromDates(editPersonnelForm.dates);
    const realHours = computeRealHoursFromDates(editPersonnelForm.dates);
    const updatedEntries = personnelEntries.map((entry, idx) =>
      idx === editingPersonnelIdx
        ? {
            ...editPersonnelForm,
            tauxHoraire: tx,
            total: tx * billedHours, // billed 7h/day
            totalHeures: realHours, // keep real hours for display
          }
        : entry
    );
    setPersonnelEntries(updatedEntries);
    setEditingPersonnelIdx(null);
    setEditPersonnelForm(null);
  };

  const cancelEditPersonnelEntry = () => {
    setEditingPersonnelIdx(null);
    setEditPersonnelForm(null);
  };

  // --- Confirmation popup handlers for transport fee (works for both create & update) ---
  const proceedSaveWithTransport = async () => {
    // proceed to save including a virtual transport entry
    try {
      setSaving(true);
      setShowTransportConfirm(false);

      if (pendingAction === "create") {
        const { personnel, recalculatedBudget } = buildPersonnelPayload(true);
        const newCharge = {
          chantierId: id,
          type: "Charges de personnel",
          name: "Charges de personnel",
          budget: recalculatedBudget,
          description,
          personnel,
        };
        const response = await createCharge(newCharge);
        if (response) {
          fetchCharges();
          resetForm();
          resetPersonnelForm();
        }
      } else if (pendingAction === "update" && editingCharge?._id) {
        const { personnel, recalculatedBudget } = buildPersonnelPayload(true);
        const updatedCharge = {
          chantierId: id,
          type: "Charges de personnel",
          name: "Charges de personnel",
          budget: recalculatedBudget,
          description,
          personnel,
        };
        const resp = await updateCharge(editingCharge._id, updatedCharge);

        // Guard: only read .json() if this is a real Response
        if (resp && typeof resp.ok === "boolean" && !resp.ok) {
          let msg = "Erreur lors de la sauvegarde";
          try {
            const data = await resp.json();
            msg = data?.message || msg;
          } catch {}
          alert(msg);
          return;
        }

        await fetchCharges();
        setEditPopupOpen(false);
        setEditingCharge(null);
        resetForm();
        resetPersonnelForm();
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde: " + error.message);
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  };

  const proceedSaveWithoutTransport = async () => {
    // proceed to save without adding transport
    try {
      setSaving(true);
      setShowTransportConfirm(false);

      if (pendingAction === "create") {
        const { personnel, recalculatedBudget } = buildPersonnelPayload(false);
        const newCharge = {
          chantierId: id,
          type: "Charges de personnel",
          name: "Charges de personnel",
          budget: recalculatedBudget,
          description,
          personnel,
        };
        const response = await createCharge(newCharge);
        if (response) {
          fetchCharges();
          resetForm();
          resetPersonnelForm();
        }
      } else if (pendingAction === "update" && editingCharge?._id) {
        const { personnel, recalculatedBudget } = buildPersonnelPayload(false);
        const updatedCharge = {
          chantierId: id,
          type: "Charges de personnel",
          name: "Charges de personnel",
          budget: recalculatedBudget,
          description,
          personnel,
        };
        const resp = await updateCharge(editingCharge._id, updatedCharge);

        // Guard: only read .json() if this is a real Response
        if (resp && typeof resp.ok === "boolean" && !resp.ok) {
          let msg = "Erreur lors de la sauvegarde";
          try {
            const data = await resp.json();
            msg = data?.message || msg;
          } catch {}
          alert(msg);
          return;
        }

        await fetchCharges();
        setEditPopupOpen(false);
        setEditingCharge(null);
        resetForm();
        resetPersonnelForm();
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde: " + error.message);
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  };

  return (
    <div className="charges-form-container">
      <div className="form-header">
        <h2>Gestion des charges - {chantier?.nomChantier}</h2>
        <button onClick={() => navigate("/")} className="btn btn-secondary">
          ← Retour
        </button>
      </div>

      {/* Add New Charge Section */}
      {chantier?.etat !== "fermé" && (
        <div className="add-charge-section">
          <h3>Ajouter une nouvelle charge</h3>
          <div className="charge-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Type de charge *:</label>
                <select
                  value={selectedChargeType}
                  onChange={handleChargeTypeChange}
                  className="form-select"
                >
                  <option value="">-- Sélectionner un type --</option>
                  {chargeTypes.map((type, index) => (
                    <option key={index} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              {selectedChargeType === "Autre" && (
                <div className="form-group">
                  <label className="form-label">Type personnalisé *:</label>
                  <input
                    type="text"
                    placeholder="Saisir le type de charge"
                    value={customChargeType}
                    onChange={(e) => setCustomChargeType(e.target.value)}
                    className="form-input"
                  />
                </div>
              )}
            </div>

            {/* Dynamic forms based on type */}
            {selectedChargeType === "Services extérieurs" && (
              <div className="services-form">
                <h4 className="subform-title">Détails Services extérieurs</h4>
                <div className="services-grid">
                  {Object.keys(servicesData).map((key) => (
                    <div className="form-group" key={key}>
                      <label className="form-label">{key}</label>
                      <input
                        type="number"
                        min="0"
                        value={servicesData[key]}
                        onChange={(e) =>
                          handleServicesChange(key, e.target.value)
                        }
                        className="form-input"
                      />
                    </div>
                  ))}
                </div>
                <div className="services-total">
                  <strong>Total: </strong>
                  <span>{servicesTotal.toFixed(2)} €</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>
                {/* Save button for ADD (not edit) */}
                {editingCharge === null && (
                  <div className="form-actions">
                    <button
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={servicesTotal === 0 || saving}
                    >
                      💾 Enregistrer la charge
                    </button>
                    <button onClick={resetForm} className="btn btn-secondary" disabled={saving}>
                      🔄 Reset
                    </button>
                    
                  </div>
                )}
              </div>
            )}

            {selectedChargeType === "Interim" && (
              <div className="interim-form">
                <h4 className="subform-title">Détails Interim</h4>
                <div className="interim-row">
                  <div className="form-group">
                    <label className="form-label">Nom de l'ouvrier</label>
                    <input
                      type="text"
                      name="nom"
                      value={interimForm.nom}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre d'heures</label>
                    <input
                      type="number"
                      name="heures"
                      min="0"
                      value={interimForm.heures}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Taux horaire (€)</label>
                    <input
                      type="number"
                      name="taux"
                      min="0"
                      value={interimForm.taux}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ alignSelf: "end", marginTop: "24px" }}
                    onClick={addInterimOuvrier}
                    disabled={
                      !interimForm.nom ||
                      !interimForm.heures ||
                      !interimForm.taux
                    }
                  >
                    + Ajouter ouvrier
                  </button>
                </div>
                {interimOuvriers.length > 0 && (
                  <div className="interim-list">
                    <table className="interim-table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Heures</th>
                          <th>Taux</th>
                          <th>Coût</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interimOuvriers.map((o, idx) => (
                          <tr key={idx}>
                            <td>{o.nom}</td>
                            <td>{o.heures}</td>
                            <td>{o.taux}</td>
                            <td>{o.cout.toFixed(2)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="interim-total">
                      <strong>Total: </strong>
                      <span>{interimTotal.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>
                {/* Save button for ADD (not edit) */}
                {editingCharge === null && (
                  <div className="form-actions">
                    <button
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={interimOuvriers.length === 0 || saving}
                    >
                      💾 Enregistrer la charge
                    </button>
                    <button onClick={resetForm} className="btn btn-secondary" disabled={saving}>
                      🔄 Reset
                    </button>
                  </div>
                )}
              </div>
            )}

            {selectedChargeType === "Achat" && (
              <div className="achat-form">
                <h4 className="subform-title">Détails Achat</h4>
                <div className="achat-input-row">
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={achatForm.date}
                      onChange={handleAchatFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fournisseur *</label>
                    <SearchableSelect
                      options={fournisseurs.map((f) => ({
                        id: f.id || f._id,
                        name: f.name || f.nom || "",
                      }))}
                      valueId={achatForm.fournisseurId}
                      valueLabel={achatForm.fournisseur}
                      placeholder="Taper pour rechercher…"
                      onChange={({ id, name }) =>
                        setAchatForm((prev) => ({
                          ...prev,
                          fournisseurId: id,
                          fournisseur: name,
                        }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">N° Bon d'enlèvement</label>
                    <input
                      type="text"
                      name="numBonEnlevement"
                      value={achatForm.numBonEnlevement}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="Ex: BE-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">N° Nexxio</label>
                    <input
                      type="text"
                      name="numeroNexxio"
                      value={achatForm.numeroNexxio}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="Ex: NX-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Montant HT *</label>
                    <input
                      type="number"
                      name="montantHT"
                      min="0"
                      step="0.01"
                      value={achatForm.montantHT}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary achat-add-btn"
                    onClick={addAchatLigne}
                    disabled={!canAddAchatLigne(achatForm)}
                  >
                    + Ajouter
                  </button>
                </div>

                {achatLignes.length > 0 && (
                  <div className="achat-table-wrapper">
                    <table className="achat-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Fournisseur</th>
                          <th>N° Bon d'enlèvement</th>
                          <th>N° Nexxio</th>
                          <th className="text-right">Montant HT</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {achatLignes.map((ligne, idx) => (
                          <tr key={idx}>
                            <td>{ligne.date || "—"}</td>
                            <td>{ligne.fournisseur || "—"}</td>
                            <td>{ligne.numBonEnlevement || "—"}</td>
                            <td>{ligne.numeroNexxio || "—"}</td>
                            <td className="text-right">{Number(ligne.montantHT).toFixed(2)} €</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-delete"
                                style={{ padding: "2px 8px", fontSize: "13px" }}
                                onClick={() => removeAchatLigne(idx)}
                              >
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="achat-total-row">
                          <td colSpan="4"><strong>Total HT</strong></td>
                          <td className="text-right"><strong>{achatTotal.toFixed(2)} €</strong></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>

                {editingCharge === null && (
                  <div className="form-actions">
                    <button
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={achatLignes.length === 0 || saving}
                    >
                      💾 Enregistrer la charge
                    </button>
                    <button onClick={resetForm} className="btn btn-secondary" disabled={saving}>
                      🔄 Reset
                    </button>
                  </div>
                )}
              </div>
            )}

            {selectedChargeType === "Charges de personnel" && (
              <div className="personnel-form" style={{ minHeight: "80vh" }}>
                <h4 className="subform-title">Détails Charges de personnel</h4>
                <div className="personnel-row">
                  <div className="form-group">
                    <label className="form-label">Salarié</label>
                    <select
                      name="salarieId"
                      value={personnelForm.salarieId}
                      onChange={handlePersonnelFormChange}
                      className="form-input"
                    >
                      <option value="">-- Sélectionner --</option>
                      {salaries
                        .filter(
                          (s) =>
                            !personnelEntries.some(
                              (p) =>
                                String(p.salarieId) === String(s._id ?? s.id) &&
                                String(p.prestationId) === String(personnelForm.prestationId)
                            )
                        )
                        .map((s, i) => (
                          <option
                            key={`${s._id || s.id || s.matricule || 's'}-${i}`}
                            value={s._id || s.id}
                          >
                            {s.nom} (Matricule: {s.matricule})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type de prestation</label>
                    <select
                      name="prestationId"
                      value={personnelForm.prestationId}
                      onChange={handlePersonnelFormChange}
                      className="form-input"
                    >
                      <option value="">-- Sélectionner --</option>
                      {prestations.map((p, i) => (
                        <option
                          key={`${p.id ?? p._id ?? p.name}-${i}`}
                          value={p.id}
                        >
                          {p.name} (Taux: {p.prix_heure} €/h)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Display chantier dates as read-only info */}
                {personnelForm.dateDebut && personnelForm.dateFin && (
                  <div className="form-group" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f8ff', borderRadius: '6px', border: '1px solid #1976d2' }}>
                    <strong style={{ color: '#1976d2' }}>Période du chantier:</strong>
                    <span style={{ marginLeft: '1rem' }}>
                      Du <strong>{personnelForm.dateDebut}</strong> au <strong>{personnelForm.dateFin}</strong>
                    </span>
                    <span style={{ marginLeft: '1rem', color: '#555' }}>
                      Mode: <strong>{isLongChantier ? 'Long chantier (jours travaillés)' : 'Court chantier (heures/jour)'}</strong>
                    </span>
                  </div>
                )}
                {/* Input UI based on chantier mode */}
                {personnelForm.dateDebut && personnelForm.dateFin && personnelForm.heuresParJour.length > 0 && (
                  isLongChantier ? (
                    <div className="personnel-row" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <button type="button" className="btn btn-secondary" onClick={() => setPersonnelForm(prev => ({ ...prev, heuresParJour: prev.heuresParJour.map(d => ({ ...d, selected: true })) }))}>Tout cocher</button>
                        <button type="button" className="btn btn-secondary" onClick={() => setPersonnelForm(prev => ({ ...prev, heuresParJour: prev.heuresParJour.map(d => ({ ...d, selected: false })) }))}>Tout décocher</button>
                      </div>
                      {personnelForm.heuresParJour.map((d, idx) => (
                        <label key={d.date} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '6px 8px' }}>
                          <input
                            type="checkbox"
                            checked={d.selected !== false}
                            onChange={(e) => setPersonnelForm(prev => {
                              const arr = [...prev.heuresParJour];
                              arr[idx] = { ...arr[idx], selected: e.target.checked };
                              return { ...prev, heuresParJour: arr };
                            })}
                          />
                          <span style={{ minWidth: 110 }}>{d.date}</span>
                        </label>
                      ))}
                      <div style={{ marginTop: 8, color: '#1976d2' }}>
                        Jours sélectionnés: <strong>{personnelForm.heuresParJour.filter(x => x.selected !== false).length}</strong> → Montant: <strong>{(140 * personnelForm.heuresParJour.filter(x => x.selected !== false).length).toFixed(2)} €</strong>
                      </div>
                    </div>
                  ) : (
                    <div className="personnel-row" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                      {personnelForm.heuresParJour.map((h, idx) => (
                        <div key={h.date} className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ minWidth: 110 }}>{h.date}</span>
                          <input type="number" min="0" max="23" placeholder="Heure début" value={h.heureDebut} onChange={(e) => handleHeureChange(idx, 'heureDebut', e.target.value)} className="form-input" style={{ width: 160 }} />
                          <span>à</span>
                          <input type="number" min="0" max="23" placeholder="Heure fin" value={h.heureFin} onChange={(e) => handleHeureChange(idx, 'heureFin', e.target.value)} className="form-input" style={{ width: 160 }} />
                        </div>
                      ))}
                    </div>
                  )
                )}
                <div className="personnel-actions-stack">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={addPersonnelEntry}
                    disabled={
                      !personnelForm.salarieId ||
                      !personnelForm.prestationId ||
                      !personnelForm.dateDebut ||
                      !personnelForm.dateFin ||
                      (isLongChantier
                        ? personnelForm.heuresParJour.filter(d => d.selected !== false).length === 0
                        : (personnelForm.heuresParJour.length === 0 || personnelForm.heuresParJour.some((h) => h.heureDebut === "" || h.heureFin === "" || isNaN(Number(h.heureDebut)) || isNaN(Number(h.heureFin))))
                      )
                    }
                  >
                    + Ajouter salarié
                  </button>

                 
                </div>

                {/* Editable personnelEntries table */}
                {personnelEntries.length > 0 && (
                  <div className="personnel-list">
                    <table className="personnel-table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Type de prestation</th>
                          <th>Taux horaire (€)</th>
                          <th>{isLongChantier ? 'Jours travaillés' : 'Heures réelles'}</th>
                          <th>Total (facturé)</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {personnelEntries.map((p, idx) => {
                          if (p.isTransportFee) {
                            const amount = Number(p.amount ?? p.total ?? 0);
                            const workingDays = p.workingDays || 1;
                            const dailyRate = workingDays > 1 ? (amount / workingDays) : amount;
                            return (
                              <tr key={`transport-${idx}`} className="transport-fee-row">
                                <td colSpan={2}>
                                  <span className="transport-badge">
                                    Frais de transport
                                    {isLongChantier && workingDays > 1 && (
                                      <span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                                        {` (${dailyRate.toFixed(2)} € × ${workingDays} jours)`}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td>—</td>
                                <td>{isLongChantier ? `${workingDays} j` : '—'}</td>
                                <td>{amount.toFixed(2)} €</td>
                                <td>
                                  <button
                                    className="btn btn-delete"
                                    style={{ padding: "2px 8px", fontSize: "13px" }}
                                    onClick={() => removePersonnelEntry(idx)}
                                  >
                                    Supprimer
                                  </button>
                                </td>
                              </tr>
                            );
                          }
                          const realHours = isLongChantier ? (p.dates?.length || 0) : (computeRealHoursFromDates(p.dates) || p.totalHeures || 0);
                          const billedTotal = computeBilledTotalForEntry(p);
                          return (
                            <tr key={idx}>
                              <td>{p.nom}</td>
                              <td>{p.prestationType}</td>
                              <td>
                                {
                                  Number(
                                    p.salarie?.taux_horaire ?? p.tauxHoraire ?? 0
                                  ).toFixed(2)
                                }
                              </td>
                              <td>{isLongChantier ? `${realHours} j` : realHours}</td>
                              <td>{billedTotal.toFixed(2)} €</td>
                              <td>
                                <button
                                  className="btn btn-edit"
                                  style={{
                                    marginRight: 6,
                                    padding: "2px 8px",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => startEditPersonnelEntry(idx)}
                                >
                                  Modifier
                                </button>
                                <button
                                  className="btn btn-delete"
                                  style={{
                                    padding: "2px 8px",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => removePersonnelEntry(idx)}
                                >
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="personnel-total">
                      <strong>Total: </strong>
                      <span>{personnelTotal.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
                {/* Inline edit personnel entry form */}
                {editingPersonnelIdx !== null && editPersonnelForm && (
                  <div
                    className="personnel-edit-form"
                    style={{
                      background: "#f7f7fb",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                    }}
                  >
                    <h5 style={{ color: "#1976d2" }}>Modifier salarié</h5>
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        name="nom"
                        value={editPersonnelForm.nom}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Type de prestation</label>
                      <select
                        name="prestationType"
                        value={editPersonnelForm.prestationType}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                      >
                        <option value="">-- Sélectionner --</option>
                        {prestations.map((p) => (
                          <option key={p.id ?? p._id ?? p.name} value={p.name}>
                            {p.name} (Taux: {p.prix_heure} €/h)
                          </option>
                        ))}
                      </select>
                    </div>
                    {!isLongChantier && (
                      <>
                        <div className="form-group">
                          <label>Taux horaire (€)</label>
                          <input type="number" name="tauxHoraire" value={editPersonnelForm.tauxHoraire} onChange={handleEditPersonnelFormChange} className="form-input" readOnly />
                        </div>
                        <div className="form-group">
                          <label>Heures totales (réelles)</label>
                          <input type="number" name="totalHeures" value={computeRealHoursFromDates(editPersonnelForm.dates)} onChange={handleEditPersonnelFormChange} className="form-input" readOnly />
                        </div>
                        <div className="form-group">
                          <label>Total (€) - facturé (7h/jour)</label>
                          <input type="number" name="total" value={computeBilledTotalForEntry(editPersonnelForm)} onChange={handleEditPersonnelFormChange} className="form-input" readOnly />
                        </div>
                      </>
                    )}
                    {isLongChantier && (
                      <div className="form-group" style={{ borderTop: '1px solid #ddd', paddingTop: 8 }}>
                        <label>Jours travaillés</label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <button type="button" className="btn btn-secondary" onClick={() => setEditPersonnelForm(prev => ({ ...prev, dates: (prev.dates || []).map(d => ({ ...d, selected: true })) }))}>Tout cocher</button>
                          <button type="button" className="btn btn-secondary" onClick={() => setEditPersonnelForm(prev => ({ ...prev, dates: (prev.dates || []).map(d => ({ ...d, selected: false })) }))}>Tout décocher</button>
                        </div>
                        {(editPersonnelForm.dates || []).map((d, i) => (
                          <label key={`${d.date}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                            <input type="checkbox" checked={d.selected !== false} onChange={(e) => setEditPersonnelForm(prev => { const arr = [...(prev.dates || [])]; arr[i] = { ...arr[i], selected: e.target.checked }; return { ...prev, dates: arr }; })} />
                            <span>{d.date}</span>
                          </label>
                        ))}
                        <div style={{ marginTop: 8, color: '#1976d2' }}>
                          Jours sélectionnés: <strong>{(editPersonnelForm.dates || []).filter(x => x.selected !== false).length}</strong> → Montant: <strong>{(140 * (editPersonnelForm.dates || []).filter(x => x.selected !== false).length).toFixed(2)} €</strong>
                        </div>
                      </div>
                    )}
                    <div className="form-actions">
                      <button
                        className="btn btn-primary"
                        onClick={saveEditPersonnelEntry}
                      >
                        Sauvegarder
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={cancelEditPersonnelEntry}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
                <div
                  className="form-actions"
                  style={{ justifyContent: "center", marginTop: "32px" }}
                >
                  {editingCharge === null ? (
                    <button
                      type="button"
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={personnelEntries.length === 0 || saving}
                      style={{
                        minWidth: 180,
                        fontSize: "1.1rem",
                        padding: "16px 32px",
                      }}
                    >
                      💾 Enregistrer la charge
                    </button>
                    
                  ) : (
                    <button
                      type="button"
                      onClick={handleUpdateCharge}
                      className="btn btn-primary"
                      disabled={personnelEntries.length === 0 || saving}
                      style={{
                        minWidth: 180,
                        fontSize: "1.1rem",
                        padding: "16px 32px",
                      }}
                    >
                      💾 Sauvegarder
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    style={{
                      minWidth: 180,
                      fontSize: "1.1rem",
                      padding: "16px 32px",
                    }}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Default form for other types */}
            {selectedChargeType &&
              selectedChargeType !== "Services extérieurs" &&
              selectedChargeType !== "Interim" &&
              selectedChargeType !== "Achat" &&
              selectedChargeType !== "Charges de personnel" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Nom de la charge *:</label>
                      <input
                        type="text"
                        placeholder="Ex: Matériaux, Personnel, Transport..."
                        value={chargeName}
                        onChange={(e) => setChargeName(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Budget *:</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Description (optionnel):
                    </label>
                    <textarea
                      placeholder="Description détaillée de la charge"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={!selectedChargeType || !chargeName || !budget || saving}
                    >
                      💾 Enregistrer la charge
                    </button>
                    <button onClick={resetForm} className="btn btn-secondary" disabled={saving}>
                      🔄 Reset
                    </button>
                  </div>
                </>
              )}
          </div>
        </div>
      )}

      {/* Charges Summary */}
      {charges.length > 0 && (
        <div className="charges-summary">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h3 style={{ margin: 0 }}>Résumé des charges</h3>
            <div className="charge-mode-toggle">
              <button
                type="button"
                className={`toggle-btn ${chargeMode === 'provisoires' ? 'active' : ''}`}
                onClick={() => setChargeMode('provisoires')}
              >
                📊 Provisoires
              </button>
              <button
                type="button"
                className={`toggle-btn ${chargeMode === 'réelles' ? 'active' : ''}`}
                onClick={() => setChargeMode('réelles')}
              >
                ✅ Réelles
              </button>
            </div>
          </div>
          <div className="charge-mode-info">
            {chargeMode === 'provisoires' ? (
              <p className="mode-description">
                <strong>Mode Provisoires:</strong> Inclut l'acompte d'achat auto-créé (30% du chiffre d'affaire)
              </p>
            ) : (
              <p className="mode-description">
                <strong>Mode Réelles:</strong> Exclut l'acompte d'achat (30%) afin d'afficher les montants réels
              </p>
            )}
          </div>
          <div className="summary-grid">
            {Object.entries(totals).map(
              ([type, amount]) =>
                type !== "total" &&
                amount > 0 && (
                  <div key={type} className="summary-item">
                    <span className="summary-type">{type}</span>
                    <span className="summary-amount">
                      {amount.toFixed(2)} €
                    </span>
                  </div>
                )
            )}
            <div className="summary-item total">
              <span className="summary-type">Total</span>
              <span className="summary-amount">
                {totals.total.toFixed(2)} €
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Existing Charges List */}
      <div className="charges-list-section">
        <h3>Charges existantes</h3>
        {charges.length === 0 ? (
          <p className="no-charges">Aucune charge ajoutée pour ce chantier.</p>
        ) : (
          <div className="charges-grid">
            {(chargeMode === 'réelles'
              ? charges.filter((c) => !(isAutoThirtyPercentCharge(c) && !isReelleTruthy(c)))
              : charges.filter((c) => !isReelleTruthy(c))
            ).map((charge) => {
              const amount =
                typeof charge.montant !== "undefined"
                  ? Number(charge.montant) || 0
                  : Number(charge.budget) || 0;
              return (
                <div key={charge._id} className={`charge-card ${charge.type === "Charges de personnel" ? 'charge-card-personnel' : ''}`}>
                  <>
                    <div className="charge-header">
                      <div className="charge-header-left">
                        <span className="charge-type-badge">
                          {charge.type === "Autre"
                            ? charge.customType
                            : charge.type}
                        </span>
                        {chargeMode === 'provisoires' && isAutoThirtyPercentCharge(charge) && (
                          <span className="auto30-badge" title="Acompte auto (30% budget)">30% auto</span>
                        )}
                      </div>
                      <div className="charge-header-actions">
                        <button
                          onClick={() => handleEditClick(charge)}
                          className="btn-icon btn-icon-edit"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(charge)}
                          className="btn-icon btn-icon-delete"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    <div className="charge-total-section">
                      <div className="charge-budget-label">Montant total</div>
                      <div className="charge-budget">{amount.toFixed(2)} €</div>
                    </div>
                    
                    {charge.description && (
                      <p className="charge-description">
                        {charge.description}
                      </p>
                    )}
                    
                    {/* Show personnel details if type is Charges de personnel */}
                    {charge.type === "Charges de personnel" &&
                      Array.isArray(charge.personnel) &&
                      charge.personnel.length > 0 && (
                        <div className="charge-personnel-details">
                          <div className="personnel-details-header">
                            <span className="personnel-count">{charge.personnel.filter(p => !p.isTransportFee).length} employé{charge.personnel.filter(p => !p.isTransportFee).length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="personnel-table-wrapper">
                            <table className="personnel-detail-table">
                              <thead>
                                <tr>
                                  <th>Nom</th>
                                  <th>Type de prestation</th>
                                  <th>Taux horaire (€)</th>
                                  <th>Heures réelles</th>
                                  <th className="text-right">Total (€) facturé</th>
                                </tr>
                              </thead>
                              <tbody>
                                {charge.personnel.map((p, idx) => {
                                  if (p.isTransportFee) {
                                    const amountT = Number(p.amount ?? p.total ?? 0);
                                    const workingDays = p.workingDays || 1;
                                    const dailyRate = workingDays > 1 ? (amountT / workingDays) : amountT;
                                    return (
                                      <tr key={`transport-view-${idx}`} className="transport-fee-row">
                                        <td colSpan={3}>
                                          <span className="transport-badge">
                                            🚚 Frais de transport
                                            {isLongChantier && workingDays > 1 && (
                                              <span className="transport-details">
                                                {` (${dailyRate.toFixed(2)} € × ${workingDays} jours)`}
                                              </span>
                                            )}
                                          </span>
                                        </td>
                                        <td className="text-center">{isLongChantier ? `${workingDays} j` : '—'}</td>
                                        <td className="text-right amount-cell">{amountT.toFixed(2)} €</td>
                                      </tr>
                                    );
                                  }
                                  const realHours = computeRealHoursFromDates(p.dates) || p.totalHeures || 0;
                                  const rate =
                                    Number(
                                      p.salarie?.taux_horaire ?? p.tauxHoraire ?? 0
                                    );
                                  const billedTotal = computeBilledTotalForEntry(p);
                                  return (
                                    <tr key={idx}>
                                      <td className="personnel-name">{p.nom}</td>
                                      <td className="personnel-prestation">{p.prestationType}</td>
                                      <td className="text-center">{rate.toFixed(2)}</td>
                                      <td className="text-center">{isLongChantier ? `${Array.isArray(p.dates) ? p.dates.filter(d => d?.date && isWorkingDay(d.date)).length : 0} j` : realHours}</td>
                                      <td className="text-right amount-cell">
                                        {billedTotal.toFixed(2)} €
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {/* Show achat eye icon to view/manage details */}
                    {charge.type === "Achat" && (
                        <div className="charge-achat-preview">
                          <button
                            className="btn-achat-view"
                            onClick={() => openAchatPopup(charge)}
                            title="Voir et gérer les détails d'achat"
                          >
                            <span className="achat-view-icon">👁️</span>
                            <span className="achat-view-label">Voir détails</span>
                            <span className="achat-count">{(Array.isArray(charge.achatLignes) ? charge.achatLignes.length : 0)} ligne{(Array.isArray(charge.achatLignes) ? charge.achatLignes.length : 0) !== 1 ? 's' : ''}</span>
                          </button>
                        </div>
                      )}
                  </>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Charge Popup */}
      <EditPopup
        isOpen={editPopupOpen}
        onClose={handleCancelEdit}
        fullScreen={
          selectedChargeType === "Charges de personnel" ||
          selectedChargeType === "Services extérieurs" ||
          selectedChargeType === "Achat"
        }
      >
        <div>
          <h3 style={{ color: "#1976d2", marginBottom: 16 }}>
            Modifier la charge
          </h3>
          <div className="charge-form">
            {/* Only show the form for the selected type */}
            {selectedChargeType === "Services extérieurs" && (
              <div className="services-form">
                <h4 className="subform-title">Détails Services extérieurs</h4>
                <div className="services-grid">
                  {Object.keys(servicesData).map((key) => (
                    <div className="form-group" key={key}>
                      <label className="form-label">{key}</label>
                      <input
                        type="number"
                        min="0"
                        value={servicesData[key]}
                        onChange={(e) =>
                          handleServicesChange(key, e.target.value)
                        }
                        className="form-input"
                      />
                    </div>
                  ))}
                </div>
                <div className="services-total">
                  <strong>Total: </strong>
                  <span>{servicesTotal.toFixed(2)} €</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>
                <div className="form-actions">
                  <button
                    onClick={handleUpdateCharge}
                    className="btn btn-primary"
                    disabled={servicesTotal === 0 || saving}
                  >
                    💾 Sauvegarder
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {selectedChargeType === "Interim" && (
              <div className="interim-form">
                <h4 className="subform-title">Détails Interim</h4>
                <div className="interim-row">
                  <div className="form-group">
                    <label className="form-label">Nom de l'ouvrier</label>
                    <input
                      type="text"
                      name="nom"
                      value={interimForm.nom}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre d'heures</label>
                    <input
                      type="number"
                      name="heures"
                      min="0"
                      value={interimForm.heures}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Taux horaire (€)</label>
                    <input
                      type="number"
                      name="taux"
                      min="0"
                      value={interimForm.taux}
                      onChange={handleInterimFormChange}
                      className="form-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ alignSelf: "end", marginTop: "24px" }}
                    onClick={addInterimOuvrier}
                    disabled={
                      !interimForm.nom ||
                      !interimForm.heures ||
                      !interimForm.taux
                    }
                  >
                    + Ajouter ouvrier
                  </button>
                </div>
                {interimOuvriers.length > 0 && (
                  <div className="interim-list">
                    <table className="interim-table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Heures</th>
                          <th>Taux</th>
                          <th>Coût</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interimOuvriers.map((o, idx) => (
                          <tr key={idx}>
                            <td>{o.nom}</td>
                            <td>{o.heures}</td>
                            <td>{o.taux}</td>
                            <td>{o.cout.toFixed(2)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="interim-total">
                      <strong>Total: </strong>
                      <span>{interimTotal.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>
                {/* In edit popup, use update */}
                <div className="form-actions">
                  <button
                    onClick={handleUpdateCharge}
                    className="btn btn-primary"
                    disabled={interimOuvriers.length === 0 || saving}
                  >
                    💾 Sauvegarder
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {selectedChargeType === "Achat" && (
              <div className="achat-form">
                <h4 className="subform-title">Détails Achat</h4>
                <div className="achat-input-row">
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={achatForm.date}
                      onChange={handleAchatFormChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fournisseur *</label>
                    <SearchableSelect
                      options={fournisseurs.map((f) => ({
                        id: f.id || f._id,
                        name: f.name || f.nom || "",
                      }))}
                      valueId={achatForm.fournisseurId}
                      valueLabel={achatForm.fournisseur}
                      placeholder="Taper pour rechercher…"
                      onChange={({ id, name }) =>
                        setAchatForm((prev) => ({
                          ...prev,
                          fournisseurId: id,
                          fournisseur: name,
                        }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">N° Bon d'enlèvement</label>
                    <input
                      type="text"
                      name="numBonEnlevement"
                      value={achatForm.numBonEnlevement}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="Ex: BE-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">N° Nexxio</label>
                    <input
                      type="text"
                      name="numeroNexxio"
                      value={achatForm.numeroNexxio}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="Ex: NX-001"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Montant HT *</label>
                    <input
                      type="number"
                      name="montantHT"
                      min="0"
                      step="0.01"
                      value={achatForm.montantHT}
                      onChange={handleAchatFormChange}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary achat-add-btn"
                    onClick={addAchatLigne}
                    disabled={!canAddAchatLigne(achatForm)}
                  >
                    + Ajouter
                  </button>
                </div>

                {achatLignes.length > 0 && (
                  <div className="achat-table-wrapper">
                    <table className="achat-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Fournisseur</th>
                          <th>N° Bon d'enlèvement</th>
                          <th>N° Nexxio</th>
                          <th className="text-right">Montant HT</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {achatLignes.map((ligne, idx) => (
                          <tr key={idx}>
                            <td>{ligne.date || "—"}</td>
                            <td>{ligne.fournisseur || "—"}</td>
                            <td>{ligne.numBonEnlevement || "—"}</td>
                            <td>{ligne.numeroNexxio || "—"}</td>
                            <td className="text-right">{Number(ligne.montantHT).toFixed(2)} €</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-delete"
                                style={{ padding: "2px 8px", fontSize: "13px" }}
                                onClick={() => removeAchatLigne(idx)}
                              >
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="achat-total-row">
                          <td colSpan="4"><strong>Total HT</strong></td>
                          <td className="text-right"><strong>{achatTotal.toFixed(2)} €</strong></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label className="form-label">Description (optionnel):</label>
                  <textarea
                    placeholder="Description détaillée de la charge"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows="2"
                  />
                </div>

                <div className="form-actions">
                  <button
                    onClick={handleUpdateCharge}
                    className="btn btn-primary"
                    disabled={achatLignes.length === 0 || saving}
                  >
                    💾 Sauvegarder
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {selectedChargeType === "Charges de personnel" && (
              <div className="personnel-form" style={{ minHeight: "80vh" }}>
                <h4 className="subform-title">Détails Charges de personnel</h4>
                <div className="personnel-row">
                  <div className="form-group">
                    <label className="form-label">Salarié</label>
                    <select
                      name="salarieId"
                      value={personnelForm.salarieId}
                      onChange={handlePersonnelFormChange}
                      className="form-input"
                    >
                      <option value="">-- Sélectionner --</option>
                      {salaries
                        .filter(
                          (s) =>
                            !personnelEntries.some(
                              (p) =>
                                String(p.salarieId) === String(s._id ?? s.id) &&
                                String(p.prestationId) === String(personnelForm.prestationId)
                            )
                        )
                        .map((s, i) => (
                          <option
                            key={`${s._id || s.id || s.matricule || 's'}-${i}`}
                            value={s._id || s.id}
                          >
                            {s.nom} (Matricule: {s.matricule})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type de prestation</label>
                    <select
                      name="prestationId"
                      value={personnelForm.prestationId}
                      onChange={handlePersonnelFormChange}
                      className="form-input"
                    >
                      <option value="">-- Sélectionner --</option>
                      {prestations.map((p, i) => (
                        <option
                          key={`${p.id ?? p._id ?? p.name}-${i}`}
                          value={p.id}
                        >
                          {p.name} (Taux: {p.prix_heure} €/h)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Display chantier dates as read-only info in edit popup */}
                {personnelForm.dateDebut && personnelForm.dateFin && (
                  <div className="form-group" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0f8ff', borderRadius: '6px', border: '1px solid #1976d2' }}>
                    <strong style={{ color: '#1976d2' }}>Période du chantier:</strong>
                    <span style={{ marginLeft: '1rem' }}>
                      Du <strong>{personnelForm.dateDebut}</strong> au <strong>{personnelForm.dateFin}</strong>
                    </span>
                  </div>
                )}
                {/* For each day, input heure début/fin */}
                {personnelForm.dateDebut &&
                  personnelForm.dateFin &&
                  personnelForm.heuresParJour.length > 0 && (
                    <div
                      className="personnel-row"
                      style={{ flexDirection: "column", gap: "0.5rem" }}
                    >
                      {personnelForm.heuresParJour.map((h, idx) => (
                        <div
                          key={h.date}
                          className="form-group"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1rem",
                          }}
                        >
                          <span style={{ minWidth: 110 }}>{h.date}</span>
                          <input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="Heure début"
                            value={h.heureDebut}
                            onChange={(e) =>
                              handleHeureChange(
                                idx,
                                "heureDebut",
                                e.target.value
                              )
                            }
                            className="form-input"
                            style={{ width: 160 }}
                          />
                          <span>à</span>
                          <input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="Heure fin"
                            value={h.heureFin}
                            onChange={(e) =>
                              handleHeureChange(idx, "heureFin", e.target.value)
                            }
                            className="form-input"
                            style={{ width: 160 }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                <div className="personnel-actions-stack">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={addPersonnelEntry}
                    disabled={
                      !personnelForm.salarieId ||
                      !personnelForm.prestationId ||
                      !personnelForm.dateDebut ||
                      !personnelForm.dateFin ||
                      personnelForm.heuresParJour.length === 0 ||
                      personnelForm.heuresParJour.some(
                        (h) =>
                          h.heureDebut === "" ||
                          h.heureFin === "" ||
                          isNaN(Number(h.heureDebut)) ||
                          isNaN(Number(h.heureFin))
                      )
                    }
                  >
                    + Ajouter salarié
                  </button>

                  {hasEligibleCamion && !fraisTransportAdded && fraisTransportTotal > 0 && (
                    <button
                      type="button"
                      className="btn btn-transport"
                      onClick={addFraisTransport}
                      title="Ajouter les frais de transport configurés à cette charge (ajout unique)"
                    >
                      🚚 Ajouter frais de transport ({isLongChantier 
                        ? `${fraisTransportTotal.toFixed(2)} € × ${calculateWorkingDaysForTransport()} jours = ${(fraisTransportTotal * calculateWorkingDaysForTransport()).toFixed(2)} €`
                        : `${fraisTransportTotal.toFixed(2)} €`})
                    </button>
                  )}

                  {fraisTransportAdded && (
                    <span className="transport-added-badge" title="Les frais de transport ont été ajoutés à cette charge.">
                      🚚 Frais de transport ajouté
                    </span>
                  )}
                </div>

                {/* Editable personnelEntries table */}
                {personnelEntries.length > 0 && (
                  <div className="personnel-list">
                    <table className="personnel-table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Type de prestation</th>
                          <th>Taux horaire (€)</th>
                          <th>Heures réelles</th>
                          <th>Total (facturé)</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {personnelEntries.map((p, idx) => {
                          if (p.isTransportFee) {
                            const amount = Number(p.amount ?? p.total ?? 0);
                            const workingDays = p.workingDays || 1;
                            const dailyRate = workingDays > 1 ? (amount / workingDays) : amount;
                            return (
                              <tr key={`transport-edit-${idx}`} className="transport-fee-row">
                                <td colSpan={2}>
                                  <span className="transport-badge">
                                    Frais de transport
                                    {isLongChantier && workingDays > 1 && (
                                      <span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                                        {` (${dailyRate.toFixed(2)} € × ${workingDays} jours)`}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td>—</td>
                                <td>{isLongChantier ? `${workingDays} j` : '—'}</td>
                                <td>{amount.toFixed(2)} €</td>
                                <td>
                                  <button
                                    className="btn btn-delete"
                                    style={{ padding: "2px 8px", fontSize: "13px" }}
                                    onClick={() => removePersonnelEntry(idx)}
                                  >
                                    Supprimer
                                  </button>
                                </td>
                              </tr>
                            );
                          }
                          const realHours = computeRealHoursFromDates(p.dates) || p.totalHeures || 0;
                          const billedTotal = computeBilledTotalForEntry(p);
                          return (
                            <tr key={idx}>
                              <td>{p.nom}{getSalarieHasCamion(p.salarie) ? <span className="camion-chip">Camion</span> : null}</td>
                              <td>{p.prestationType}</td>
                              <td>
                                {
                                  Number(
                                    p.salarie?.taux_horaire ?? p.tauxHoraire ?? 0
                                  ).toFixed(2)
                                }
                              </td>
                              <td>{realHours}</td>
                              <td>{billedTotal.toFixed(2)} €</td>
                              <td>
                                <button
                                  className="btn btn-edit"
                                  style={{
                                    marginRight: 6,
                                    padding: "2px 8px",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => startEditPersonnelEntry(idx)}
                                >
                                  Modifier
                                </button>
                                <button
                                  className="btn btn-delete"
                                  style={{
                                    padding: "2px 8px",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => removePersonnelEntry(idx)}
                                >
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="personnel-total">
                      <strong>Total: </strong>
                      <span>{personnelTotal.toFixed(2)} €</span>
                    </div>
                  </div>
                )}
                {/* Inline edit personnel entry form */}
                {editingPersonnelIdx !== null && editPersonnelForm && (
                  <div
                    className="personnel-edit-form"
                    style={{
                      background: "#f7f7fb",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                    }}
                  >
                    <h5 style={{ color: "#1976d2" }}>Modifier salarié</h5>
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        name="nom"
                        value={editPersonnelForm.nom}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Type de prestation</label>
                      <select
                        name="prestationType"
                        value={editPersonnelForm.prestationType}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                      >
                        <option value="">-- Sélectionner --</option>
                        {prestations.map((p) => (
                          <option key={p.id ?? p._id ?? p.name} value={p.name}>
                            {p.name} (Taux: {p.prix_heure} €/h)
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Taux horaire (€)</label>
                      <input
                        type="number"
                        name="tauxHoraire"
                        value={editPersonnelForm.tauxHoraire}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>Heures totales (réelles)</label>
                      <input
                        type="number"
                        name="totalHeures"
                        value={computeRealHoursFromDates(editPersonnelForm.dates)}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                        readOnly
                      />
                    </div>
                    <div className="form-group">
                      <label>Total (€) - facturé (7h/jour)</label>
                      <input
                        type="number"
                        name="total"
                        value={computeBilledTotalForEntry(editPersonnelForm)}
                        onChange={handleEditPersonnelFormChange}
                        className="form-input"
                        readOnly
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-primary"
                        onClick={saveEditPersonnelEntry}
                      >
                        Sauvegarder
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={cancelEditPersonnelEntry}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
                <div
                  className="form-actions"
                  style={{ justifyContent: "center", marginTop: "32px" }}
                >
                  {editingCharge === null ? (
                    <button
                      type="button"
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={personnelEntries.length === 0 || saving}
                      style={{
                        minWidth: 180,
                        fontSize: "1.1rem",
                        padding: "16px 32px",
                      }}
                    >
                      💾 Enregistrer la charge
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleUpdateCharge}
                      className="btn btn-primary"
                      disabled={personnelEntries.length === 0 || saving}
                      style={{
                        minWidth: 180,
                        fontSize: "1.1rem",
                        padding: "16px 32px",
                      }}
                    >
                      💾 Sauvegarder
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="btn btn-secondary"
                    style={{
                      minWidth: 180,
                      fontSize: "1.1rem",
                      padding: "16px 32px",
                    }}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Default form for other types */}
            {selectedChargeType &&
              selectedChargeType !== "Services extérieurs" &&
              selectedChargeType !== "Interim" &&
              selectedChargeType !== "Achat" &&
              selectedChargeType !== "Charges de personnel" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Nom de la charge *:</label>
                      <input
                        type="text"
                        placeholder="Ex: Matériaux, Personnel, Transport..."
                        value={chargeName}
                        onChange={(e) => setChargeName(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Budget *:</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Description (optionnel):
                    </label>
                    <textarea
                      placeholder="Description détaillée de la charge"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={handleSaveCharge}
                      className="btn btn-primary"
                      disabled={!selectedChargeType || !chargeName || !budget || saving}
                    >
                      💾 Enregistrer la charge
                    </button>
                    <button onClick={resetForm} className="btn btn-secondary" disabled={saving}>
                      🔄 Reset
                    </button>
                  </div>
                </>
              )}
          </div>
        </div>
      </EditPopup>

      {/* Confirmation popup for Transport fee when saving personnel charges */}
      <ConfirmTransportPopup
        open={showTransportConfirm}
        amount={fraisTransportTotal}
        isLongChantier={isLongChantier}
        workingDays={calculateWorkingDaysForTransport()}
        onAdd={proceedSaveWithTransport}
        onSkip={proceedSaveWithoutTransport}
      />

      {/* Confirmation popup for deleting a charge */}
      <ConfirmDeletePopup
        open={confirmDelete.open}
        name={confirmDelete.name}
        onCancel={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
      />

      {/* Interactive popup for Achat details (view, add, delete rows) */}
      {viewAchatCharge && (
        <div
          className="popup-overlay"
          style={{
            zIndex: 10000,
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.5)",
            padding: "1rem",
          }}
          onClick={closeAchatPopup}
        >
          <div
            className="popup-content achat-view-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1060px, 96vw)",
              maxHeight: "90vh",
              overflow: "visible",
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
              padding: 0,
            }}
          >
            <div className="achat-popup-header">
              <div className="achat-popup-title-row">
                <span className="achat-popup-icon">🛒</span>
                <div>
                  <h3 className="achat-popup-title">Détails Achat</h3>
                  <span className="achat-popup-subtitle">
                    {popupAchatLignes.length} ligne{popupAchatLignes.length !== 1 ? 's' : ''} d'achat — Total: {popupAchatTotal.toFixed(2)} €
                  </span>
                </div>
              </div>
              <button
                className="achat-popup-close"
                onClick={closeAchatPopup}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="achat-popup-body">
              {/* Existing rows table */}
              {popupAchatLignes.length > 0 ? (
                <div className="achat-table-wrapper">
                  <table className="achat-detail-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Fournisseur</th>
                        <th>N° Bon d'enlèvement</th>
                        <th>N° Nexxio</th>
                        <th className="text-right">Montant HT</th>
                        <th className="text-center" style={{ width: 60 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popupAchatLignes.map((ligne, idx) => {
                        let dateVal = ligne.date || "—";
                        if (dateVal !== "—" && dateVal.includes("T")) dateVal = dateVal.split("T")[0];
                        return (
                        <tr key={idx}>
                          <td>{dateVal}</td>
                          <td>{ligne.fournisseur || "—"}</td>
                          <td>{ligne.numBonEnlevement || "—"}</td>
                          <td>{ligne.numeroNexxio || "—"}</td>
                          <td className="text-right amount-cell">{Number(ligne.montantHT || 0).toFixed(2)} €</td>
                          <td className="text-center">
                            <button
                              className="btn-popup-achat-delete"
                              onClick={() => removePopupAchatLigne(idx)}
                              title="Supprimer cette ligne"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="achat-total-row">
                        <td colSpan="5"><strong>Total HT</strong></td>
                        <td className="text-right"><strong>{popupAchatTotal.toFixed(2)} €</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p style={{ color: "#888", textAlign: "center", padding: "1.5rem 0" }}>Aucune ligne d'achat. Ajoutez-en ci-dessous.</p>
              )}

              {/* Add new row form */}
              <div className="achat-popup-add-section">
                <h4 className="achat-popup-add-title">➕ Ajouter une ligne</h4>
                <div className="achat-add-grid">
                  <div className="achat-add-field achat-add-field--date">
                    <label className="achat-add-label">📅 Date</label>
                    <input
                      type="date"
                      name="date"
                      value={popupAchatForm.date}
                      onChange={handlePopupAchatFormChange}
                      className="achat-add-input"
                    />
                  </div>
                  <div className="achat-add-field achat-add-field--fournisseur">
                    <label className="achat-add-label">🏢 Fournisseur *</label>
                    <SearchableSelect
                      inputClassName="achat-add-input achat-add-input--fournisseur"
                      options={fournisseurs.map((f) => ({
                        id: f.id || f._id,
                        name: f.name || f.nom || "",
                      }))}
                      valueId={popupAchatForm.fournisseurId}
                      valueLabel={popupAchatForm.fournisseur}
                      placeholder="Taper pour rechercher…"
                      onChange={({ id, name }) =>
                        setPopupAchatForm((prev) => ({
                          ...prev,
                          fournisseurId: id,
                          fournisseur: name,
                        }))
                      }
                    />
                  </div>
                  <div className="achat-add-field achat-add-field--bon">
                    <label className="achat-add-label">📋 N° Bon d'enlèvement</label>
                    <input
                      type="text"
                      name="numBonEnlevement"
                      placeholder="Ex: BE-2026-001"
                      value={popupAchatForm.numBonEnlevement}
                      onChange={handlePopupAchatFormChange}
                      className="achat-add-input"
                    />
                  </div>
                  <div className="achat-add-field achat-add-field--nexxio">
                    <label className="achat-add-label">🔖 N° Nexxio</label>
                    <input
                      type="text"
                      name="numeroNexxio"
                      placeholder="Ex: NEX-456"
                      value={popupAchatForm.numeroNexxio}
                      onChange={handlePopupAchatFormChange}
                      className="achat-add-input"
                    />
                  </div>
                  <div className="achat-add-field achat-add-field--montant">
                    <label className="achat-add-label">💰 Montant HT (€)</label>
                    <input
                      type="number"
                      name="montantHT"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={popupAchatForm.montantHT}
                      onChange={handlePopupAchatFormChange}
                      className="achat-add-input achat-add-input--montant"
                    />
                  </div>
                  <div className="achat-add-field achat-add-field--action">
                    <button
                      className="btn-popup-achat-add"
                      onClick={addPopupAchatLigne}
                      disabled={!canAddAchatLigne(popupAchatForm)}
                      title="Ajouter cette ligne"
                    >
                      <span className="btn-popup-achat-add-icon">+</span>
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="achat-popup-footer">
              <button
                className="btn btn-secondary"
                onClick={closeAchatPopup}
                disabled={popupAchatSaving}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={savePopupAchatChanges}
                disabled={popupAchatSaving}
              >
                {popupAchatSaving ? "⏳ Sauvegarde..." : "💾 Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ChargesForm;